// Pure immutable mutators for optimistic rule updates.
//
// They never mutate the input — `setQueryData` and React reconciliation rely
// on referential equality of unchanged branches, so unchanged subtrees keep
// their old reference.
//
// We intentionally cover only the text/value edits where optimistic UX is most
// valuable (input fields the user is actively typing into). Create, delete, and
// reorder mutations let TanStack invalidate-on-settled handle the re-sync:
// they're rarely fast enough to flicker and the local IDs from the server
// response can't be pre-computed.

// Walk every query in a rule (top-level + recursive sub-queries) and apply a
// mapper; mapper returns the new query or the same reference if unchanged.
function mapQueries(rule, mapper) {
  if (!rule) return rule;
  const mapOne = (q) => {
    if (!q) return q;
    const next = mapper(q) ?? q;
    if (next.queries?.length) {
      const subs = next.queries.map(mapOne);
      const changed = subs.some((s, i) => s !== next.queries[i]);
      return changed ? { ...next, queries: subs } : next;
    }
    return next;
  };
  if (!rule.queries?.length) return rule;
  const mapped = rule.queries.map(mapOne);
  const changed = mapped.some((q, i) => q !== rule.queries[i]);
  return changed ? { ...rule, queries: mapped } : rule;
}

// Walk every condition field in a query's conditionGroups + joinGroups
// (recursively into nested groups) and apply a mapper to each field; mapper
// returns the new field or the same reference if unchanged.
function mapFields(query, mapper) {
  const walkGroups = (groups) => {
    if (!groups?.length) return groups;
    const nextGroups = groups.map((g) => {
      if (!g || typeof g !== "object") return g;
      let next = g;
      if (g.fields?.length) {
        const fields = g.fields.map((f) => mapper(f) ?? f);
        const fieldsChanged = fields.some((f, i) => f !== g.fields[i]);
        if (fieldsChanged) next = { ...next, fields };
      }
      if (next.groups?.length) {
        const subs = walkGroups(next.groups);
        if (subs !== next.groups) next = { ...next, groups: subs };
      }
      return next;
    });
    const changed = nextGroups.some((g, i) => g !== groups[i]);
    return changed ? nextGroups : groups;
  };

  const cg = walkGroups(query.conditionGroups);
  const jg = walkGroups(query.joinGroups);
  if (cg === query.conditionGroups && jg === query.joinGroups) return query;
  return { ...query, conditionGroups: cg, joinGroups: jg };
}

// ── Public mutators ──────────────────────────────────────────────────────────

export function setEntityName(rule, displayedEntityId, name) {
  if (!rule || !displayedEntityId) return rule;
  return mapQueries(rule, (q) => {
    if (!q.displayedEntities?.length) return q;
    const next = q.displayedEntities.map((e) =>
      e?.diplayedEntityId === displayedEntityId ? { ...e, name } : e,
    );
    const changed = next.some((e, i) => e !== q.displayedEntities[i]);
    return changed ? { ...q, displayedEntities: next } : q;
  });
}

export function setQueryAlias(rule, queryId, alias) {
  if (!rule || !queryId) return rule;
  return mapQueries(rule, (q) => (q.id === queryId ? { ...q, alias } : q));
}

export function setQueryLimit(rule, queryId, limit) {
  if (!rule || !queryId) return rule;
  return mapQueries(rule, (q) =>
    q.id === queryId ? { ...q, limit: { ...(q.limit || {}), limit } } : q,
  );
}

// Apply a partial update to a single condition field by id (value / operator /
// sign / interval edits).
export function patchConditionField(rule, fieldId, patch) {
  if (!rule || !fieldId || !patch) return rule;
  return mapQueries(rule, (q) =>
    mapFields(q, (f) => (f?.id === fieldId ? { ...f, ...patch } : f)),
  );
}

// Set the displayed-entity-field name (the alias shown in chips).
export function setConditionFieldName(rule, fieldId, name) {
  return patchConditionField(rule, fieldId, { name });
}

// Reorder Data Fields locally, ahead of the server round-trip — `fields` is
// the same `{id, rowNumber}[]` payload sent to the reorder endpoint. Applied
// to whichever query's dataFields list contains these ids.
export function reorderDataFieldsInCache(rule, fields) {
  if (!rule || !fields?.length) return rule;
  const rowNumberById = new Map(fields.map((f) => [f.id, f.rowNumber]));
  return mapQueries(rule, (q) => {
    if (!q.dataFields?.some((f) => rowNumberById.has(f.id))) return q;
    const next = [...q.dataFields].sort(
      (a, b) =>
        (rowNumberById.get(a.id) ?? 0) - (rowNumberById.get(b.id) ?? 0),
    );
    const changed = next.some((f, i) => f !== q.dataFields[i]);
    return changed ? { ...q, dataFields: next } : q;
  });
}
